import React, { useState, useEffect } from 'react';
import './ScheduledMessagePicker.css';

const ScheduledMessagePicker = ({ conversationId, onClose, onScheduled }) => {
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchScheduledMessages();
    
    // Set default to 1 hour from now
    const now = new Date();
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
    
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().slice(0, 5);
    
    setScheduledDate(dateStr);
    setScheduledTime(timeStr);
  }, [conversationId]);

  const fetchScheduledMessages = async () => {
    try {
      const response = await fetch(
        `/api/messages/scheduled?conversationId=${conversationId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setScheduledMessages(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching scheduled messages:', err);
    }
  };

  const scheduleMessage = async () => {
    if (!messageContent.trim()) {
      alert('Please enter a message');
      return;
    }

    if (!scheduledDate || !scheduledTime) {
      alert('Please select date and time');
      return;
    }

    const scheduledFor = new Date(`${scheduledDate}T${scheduledTime}`);
    if (scheduledFor <= new Date()) {
      alert('Scheduled time must be in the future');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/messages/schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          conversationId,
          content: messageContent,
          scheduledFor: scheduledFor.toISOString()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setScheduledMessages(prev => [...prev, data.data]);
        setMessageContent('');
        if (onScheduled) onScheduled(data.data);
        alert('Message scheduled successfully!');
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to schedule message');
      }
    } catch (err) {
      alert('Failed to schedule message');
    } finally {
      setLoading(false);
    }
  };

  const cancelScheduledMessage = async (messageId) => {
    if (!window.confirm('Cancel this scheduled message?')) return;

    try {
      const response = await fetch(`/api/messages/scheduled/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setScheduledMessages(prev => prev.filter(m => m._id !== messageId));
        alert('Scheduled message cancelled');
      }
    } catch (err) {
      alert('Failed to cancel scheduled message');
    }
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="scheduled-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📅 Schedule Message</h2>
          <button onClick={onClose} className="modal-close-btn">✕</button>
        </div>

        <div className="scheduled-content">
          <div className="schedule-form">
            <h3>New Scheduled Message</h3>
            <textarea
              value={messageContent}
              onChange={e => setMessageContent(e.target.value)}
              placeholder="Type your message..."
              maxLength={2000}
              rows={4}
              className="message-textarea"
            />
            <div className="char-count">
              {messageContent.length}/2000
            </div>

            <div className="datetime-row">
              <div className="form-field">
                <label>Date</label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={e => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="date-input"
                />
              </div>
              <div className="form-field">
                <label>Time</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={e => setScheduledTime(e.target.value)}
                  className="time-input"
                />
              </div>
            </div>

            <button
              onClick={scheduleMessage}
              className="schedule-btn"
              disabled={loading || !messageContent.trim()}
            >
              {loading ? 'Scheduling...' : '📤 Schedule Message'}
            </button>
          </div>

          <div className="scheduled-list">
            <h3>Pending Messages ({scheduledMessages.length})</h3>
            {scheduledMessages.length > 0 ? (
              <div className="messages-list">
                {scheduledMessages.map(msg => (
                  <div key={msg._id} className="scheduled-message-item">
                    <div className="message-info">
                      <div className="message-preview">{msg.content}</div>
                      <div className="message-time">
                        ⏰ {formatDateTime(msg.scheduledFor)}
                      </div>
                    </div>
                    <button
                      className="cancel-btn"
                      onClick={() => cancelScheduledMessage(msg._id)}
                    >
                      Cancel
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">No scheduled messages</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduledMessagePicker;
